/** Map investment API payloads to investor UI shapes. */
import { normalizeTimelineItems } from "./timeline";

function mapRiskLevel(level) {
  if (!level) return "Moderate";
  const normalized = String(level).toLowerCase();
  if (normalized === "low") return "Low";
  if (normalized === "high") return "High";
  return "Moderate";
}

export function mapInvestorOpportunityListItem(item) {
  if (!item) return null;
  const slug = item.slug || String(item.id);
  return {
    id: slug,
    apiId: item.id,
    slug,
    name: item.title,
    description: item.short_description || "",
    unitPriceNgn: Number(item.unit_price) || 0,
    availableUnits: Number(item.available_units) || 0,
    totalUnits: Number(item.total_units ?? item.available_units) || 0,
    minUnitsPurchase: Number(item.minimum_units) || 1,
    maxUnitsPurchase: Number(item.maximum_units ?? item.available_units) || 999,
    expectedReturnPct: Number(item.expected_return_percentage) || 0,
    durationMonths: Number(item.duration_months) || 12,
    riskLevel: mapRiskLevel(item.risk_level),
    category: item.category || "Cooperative",
    investmentType: item.category || "Investment",
    status: item.status,
    coverImage: item.cover_image,
    canSubmitRequest: Boolean(item.can_submit_request),
    investUnavailableReason: item.invest_unavailable_reason || null,
  };
}

/** True when the opportunity is open for new investment requests. */
export function isInvestable(opportunity) {
  return Boolean(opportunity?.canSubmitRequest);
}

export function mapInvestorOpportunityDetail(item) {
  const base = mapInvestorOpportunityListItem(item);
  if (!base) return null;

  const content = item.public_content || {};
  const overview = content.overview || {};

  return {
    ...base,
    fullDescription: item.full_description || base.description,
    providerName: item.provider_name || "—",
    payoutType: item.payout_type ? String(item.payout_type).replace(/_/g, " ") : "—",
    investmentType: item.category || base.investmentType || "Investment",
    earlyExitPolicy: content.earlyExitPolicy || "Not specified",
    fundingOpenDate: item.funding_open_date,
    fundingCloseDate: item.funding_close_date,
    investmentStartDate: item.investment_start_date,
    maturityDate: item.maturity_date,
    payoutDate: item.payout_date,
    fundedAmount: Number(item.funded_amount) || 0,
    fundingTarget: Number(item.funding_target) || 0,
    currency: item.currency || "NGN",
    canSubmitRequest: Boolean(item.can_submit_request),
    investUnavailableReason: item.invest_unavailable_reason || null,
    marketingDescription: content.marketingDescription || item.short_description,
    coverImage: item.cover_image || content.coverImage,
    overview: typeof overview === "object" ? overview : { what: overview },
    highlights: content.highlights || [],
    howItWorks: content.howItWorks || [],
    gallery: content.gallery || [],
    fundingTimeline: content.milestones || content.fundingTimeline || [],
    faqs: content.faqs || [],
    riskDisclaimer: content.riskDisclaimer || "",
    documents: (item.documents || []).map((doc) => ({
      id: doc.id,
      title: doc.title,
      description: doc.document_type,
      type: doc.mime_type || "PDF",
      url: doc.file_path,
    })),
    updates: item.updates || [],
    support: {},
    terms: content.terms,
    fundsUsage: content.fundsUsage || overview.useOfFunds,
  };
}

export function mapInvestorInvestmentRequest(item) {
  if (!item) return null;
  return {
    id: item.id,
    reference: item.request_reference,
    type: "investment",
    title: item.opportunity_title,
    opportunityId: item.opportunity_slug || item.opportunity_id,
    opportunityApiId: item.opportunity_id,
    units: item.units,
    unitPriceNgn: Number(item.unit_price) || 0,
    totalNgn: Number(item.total_amount) || 0,
    amountPaid: item.amount_paid != null ? Number(item.amount_paid) : null,
    isPaid: Boolean(item.is_paid || item.paid_at),
    paidAt: item.paid_at,
    paymentReference: item.payment_reference,
    expectedReturnPct: Number(item.expected_return_snapshot) || 0,
    projectedPayoutNgn: Number(item.projected_payout_snapshot) || 0,
    status: item.status,
    fullName: item.full_name,
    phone: item.phone,
    contactMethod: item.contact_method,
    note: item.note,
    submittedAt: item.submitted_at,
    timeline: normalizeTimelineItems(item.timeline),
  };
}

export function buildInvestmentRequestPayload(values, opportunity, extras = {}) {
  return {
    units: Number(values.units),
    full_name: values.full_name,
    phone: values.phone,
    contact_method: values.contact_method,
    note: values.note || undefined,
    payment_provider: extras.payment_provider || undefined,
    frontend_base_url: extras.frontend_base_url || undefined,
  };
}

/** List shape for InvestorRequestsList */
export function mapInvestorRequestToListShape(item) {
  if (!item) return null;
  return {
    id: item.id,
    reference: item.reference,
    type: "investment",
    status: item.status,
    created_at: item.submittedAt,
    isPaid: item.isPaid,
    amountPaid: item.amountPaid,
    payload: {
      title: item.title,
      totalNgn: item.totalNgn,
      units: item.units,
      unitPriceNgn: item.unitPriceNgn,
      opportunityId: item.opportunityId,
      phone: item.phone,
      contact_method: item.contactMethod,
      investorName: item.fullName,
      amountPaid: item.amountPaid,
      isPaid: item.isPaid,
    },
  };
}

/** Detail shape for InvestorRequestDetailContent */
export function mapInvestorRequestToDetailShape(item) {
  const list = mapInvestorRequestToListShape(item);
  if (!list) return null;
  return {
    ...list,
    reference: item.reference,
    timeline: normalizeTimelineItems(item.timeline),
    submittedAt: item.submittedAt,
    payload: {
      ...list.payload,
      expectedReturnPct: item.expectedReturnPct,
      projectedPayoutNgn: item.projectedPayoutNgn,
      note: item.note,
    },
  };
}

export function staffDetailToInvestorOpportunity(staff) {
  if (!staff) return null;
  const risk = mapRiskLevel(staff.riskLevel);
  const overviewRaw = staff.overview;
  const overview =
    typeof overviewRaw === "object" && overviewRaw !== null
      ? overviewRaw
      : { what: overviewRaw || staff.shortDescription || "" };

  return {
    id: staff.slug || String(staff.id),
    apiId: staff.id,
    slug: staff.slug,
    name: staff.title,
    description: staff.shortDescription || staff.fullDescription || "",
    fullDescription: staff.fullDescription || staff.shortDescription || "",
    unitPriceNgn: Number(staff.unitPriceNgn) || 0,
    availableUnits: Number(staff.availableUnits) || 0,
    totalUnits: Number(staff.totalUnits) || 0,
    minUnitsPurchase: Number(staff.minUnits) || 1,
    maxUnitsPurchase: Number(staff.maxUnits) || Number(staff.availableUnits) || 100,
    expectedReturnPct: Number(staff.expectedReturnPct) || 0,
    durationMonths: Number(staff.durationMonths) || 12,
    riskLevel: risk,
    category: staff.category || "Cooperative",
    investmentType: staff.category || "Investment",
    providerName: staff.providerName || "—",
    payoutType: staff.payoutType
      ? String(staff.payoutType).replace(/_/g, " ")
      : "—",
    currency: staff.currency || "NGN",
    fundingOpenDate: staff.fundingOpenDate,
    fundingCloseDate: staff.fundingCloseDate,
    investmentStartDate: staff.investmentStartDate,
    maturityDate: staff.maturityDate || staff.returnPayoutDate,
    payoutDate: staff.returnPayoutDate,
    coverImage: staff.coverImageUrl,
    marketingDescription: staff.shortDescription || staff.fullDescription || "",
    overview,
    highlights: staff.highlights || [],
    howItWorks: staff.howItWorks || [],
    gallery: staff.gallery || [],
    fundingTimeline: staff.milestones || [],
    faqs: staff.faqs || [],
    riskDisclaimer: staff.riskDisclaimer || "",
    documents: (staff.documents || []).map((doc) => ({
      id: doc.id,
      title: doc.title,
      description: doc.fileType || doc.document_type || doc.description,
      type: doc.fileType || doc.mime_type || "PDF",
      url: doc.url || doc.file_path || null,
    })),
    support: {},
    terms: staff.terms,
    fundsUsage: staff.fundsUsage,
    fundedAmount: Number(staff.fundedAmountNgn) || 0,
    fundingTarget: Number(staff.fundingTargetNgn) || 0,
    status: staff.status,
    canSubmitRequest: Boolean(staff.canAcceptRequests),
    investUnavailableReason: staff.canAcceptRequests
      ? null
      : staff.notReadyReason || "This opportunity isn’t open for investment yet.",
    _staffPreview: true,
  };
}
